import React, { useState } from 'react';
import { motion, useMotionValue, animate, useTransform } from 'framer-motion';
import { Trash2, Edit } from 'lucide-react';

/**
 * Swipeable list row that reveals an action on each side.
 *
 * Props:
 *   onSwipeLeft  — called when user passes the left-action threshold (~80px)
 *   onSwipeRight — called when user passes the right-action threshold (~80px)
 *   leftLabel / rightLabel   — action text
 *   leftIcon   / rightIcon    — Lucide icon component to render on each side
 *   leftBgClass / rightBgClass — Tailwind class for the reveal panel background
 *   disabled — disables swipe (e.g. when an action menu is open)
 *
 * Snaps back to center after the action fires. Only enabled on touch devices.
 */
const ACTION_THRESHOLD = 80;
const ACTION_MAX = 100;

function useIsTouch() {
  const [touch, setTouch] = useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    setTouch(window.matchMedia('(pointer: coarse)').matches);
  }, []);
  return touch;
}

export function SwipeableRow({
  children,
  onSwipeLeft,
  onSwipeRight,
  leftLabel = 'Delete',
  rightLabel = 'Edit',
  leftIcon: LeftIcon = Trash2,
  rightIcon: RightIcon = Edit,
  leftBgClass = 'bg-red-600',
  rightBgClass = 'bg-[#0047AB]',
  disabled = false,
  className = '',
  testId,
}) {
  const x = useMotionValue(0);
  const isTouch = useIsTouch();
  // Color intensity ramps up as user drags toward the threshold.
  const rightOpacity = useTransform(x, [-ACTION_MAX, -ACTION_THRESHOLD, 0], [1, 0.85, 0]);
  const leftOpacity = useTransform(x, [0, ACTION_THRESHOLD, ACTION_MAX], [0, 0.85, 1]);

  // No-op on desktop — fall back to plain children rendering.
  if (!isTouch || disabled || (!onSwipeLeft && !onSwipeRight)) {
    return <div className={className} data-testid={testId}>{children}</div>;
  }

  const handleDragEnd = (_event, info) => {
    const offset = info.offset.x;
    if (offset <= -ACTION_THRESHOLD && onSwipeLeft) {
      animate(x, -ACTION_MAX, { duration: 0.12 });
      setTimeout(() => {
        onSwipeLeft();
        animate(x, 0, { duration: 0.18 });
      }, 100);
      return;
    }
    if (offset >= ACTION_THRESHOLD && onSwipeRight) {
      animate(x, ACTION_MAX, { duration: 0.12 });
      setTimeout(() => {
        onSwipeRight();
        animate(x, 0, { duration: 0.18 });
      }, 100);
      return;
    }
    animate(x, 0, { type: 'spring', stiffness: 320, damping: 28 });
  };

  return (
    <div className={`relative overflow-hidden ${className}`} data-testid={testId}>
      {/* Right-side panel (revealed by swiping LEFT) */}
      {onSwipeLeft && (
        <motion.div
          aria-hidden
          style={{ opacity: rightOpacity }}
          className={`absolute inset-y-0 right-0 w-[100px] ${leftBgClass} text-white flex items-center justify-center gap-1 text-xs font-semibold pointer-events-none`}
        >
          <LeftIcon className="w-4 h-4" /> {leftLabel}
        </motion.div>
      )}
      {/* Left-side panel (revealed by swiping RIGHT) */}
      {onSwipeRight && (
        <motion.div
          aria-hidden
          style={{ opacity: leftOpacity }}
          className={`absolute inset-y-0 left-0 w-[100px] ${rightBgClass} text-white flex items-center justify-center gap-1 text-xs font-semibold pointer-events-none`}
        >
          <RightIcon className="w-4 h-4" /> {rightLabel}
        </motion.div>
      )}
      <motion.div
        drag="x"
        dragConstraints={{ left: -ACTION_MAX, right: ACTION_MAX }}
        dragElastic={0.05}
        style={{ x }}
        onDragEnd={handleDragEnd}
        className="relative bg-white dark:bg-slate-900"
      >
        {children}
      </motion.div>
    </div>
  );
}
